import { AddIcon, DeleteIcon, InfoIcon } from '@chakra-ui/icons';
import {
  Button,
  ButtonGroup,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Text,
  Tag,
  HStack,
  Box,
} from '@chakra-ui/react';
import { t, Trans } from '@lingui/macro';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Api } from 'api';
import { DataTable } from 'components';
import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { Announcement, ExpertisePoint, UtilQueryCacheKey } from 'types';

export interface AdminAnnouncementsTableProps {
  announcements: Announcement[];
  isAdmin: boolean;
  onDeleteClicked?: (announcement: Announcement) => void;
  onAddAnnouncementClicked?: () => void;
  refetch: () => void;
}

const ExpertisePointTags: React.FC<{
  isAllSend?: boolean;
  expertisePoints?: ExpertisePoint[];
}> = ({ isAllSend, expertisePoints }) => {
  if (!expertisePoints) {
    return null;
  }

  if (isAllSend) {
    return (
      <Box my={2}>
        <Tag colorScheme="teal">
          <Trans>Tüm Bayiler</Trans>
        </Tag>
      </Box>
    );
  }
  return (
    <Box my={2}>
      <Text mb={2}>
        <Trans>Bayiler</Trans>
      </Text>
      <HStack spacing={4}>
        {expertisePoints.map((expertisePoint) => (
          <Tag colorScheme="teal" key={`aept${expertisePoint.name}`}>
            {expertisePoint.name}
          </Tag>
        ))}
      </HStack>
    </Box>
  );
};
export const AdminAnnouncementsTable: React.FC<AdminAnnouncementsTableProps> = ({
  announcements,
  onAddAnnouncementClicked,
  isAdmin,
  onDeleteClicked,
  refetch,
}) => {
  const [focusedAnnouncement, setFocusedAnnouncement] = useState<Announcement>();
  const columnHelper = createColumnHelper<Announcement>();
  const {
    isOpen: isDetailModalOpen,
    onOpen: onDetailModalOpen,
    onClose: onDetailModalClose,
  } = useDisclosure({
    onClose: () => {
      if (focusedAnnouncement?.isRead === false) {
        refetch();
      }
    },
  });

  const queryClient = useQueryClient();

  const markAsRead = async (announcementId: number) => {
    await Api.Util.markAnnouncementAsRead(announcementId);
    // Invalidate both announcements and unread announcements cache
    queryClient.invalidateQueries([UtilQueryCacheKey.GetAnnouncements]);
    queryClient.invalidateQueries([UtilQueryCacheKey.GetUnreadAnnouncemenets]);
  };

  const columns: ColumnDef<Announcement, string>[] = [
    columnHelper.accessor('title', {
      header: t`Duyuru`,
      cell: (info) => {
        const { original } = info.row;
        // Show read/unread status for both admin and non-admin users
        return <Text fontWeight={original.isRead ? 'light' : 'bold'}>{original.title}</Text>;
      },
    }),
    columnHelper.accessor('title', {
      header: t`Tarih`,
      cell: (info) => {
        const { original } = info.row;
        return original.createdAt.toLocaleDateString('tr-TR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        });
      },
    }),
    {
      header: t`Aksiyonlar`,
      cell: (info) => {
        const { original } = info.row;
        return (
          <ButtonGroup>
            <IconButton
              icon={<InfoIcon />}
              aria-label="Detay"
              onClick={() => {
                setFocusedAnnouncement(original);
                markAsRead(original.id);
                onDetailModalOpen();
              }}
            />
            {isAdmin && (
              <IconButton
                icon={<DeleteIcon />}
                aria-label="Fil"
                onClick={() => {
                  if (onDeleteClicked) {
                    onDeleteClicked(original);
                  }
                }}
              />
            )}
          </ButtonGroup>
        );
      },
    },
  ];
  const actionButtons = (
    <Button
      leftIcon={<AddIcon />}
      aria-label="Ekle"
      onClick={() => {
        if (onAddAnnouncementClicked) {
          onAddAnnouncementClicked();
        }
      }}
    >
      <Trans>Ekle</Trans>
    </Button>
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={announcements || []}
        tableProps={{
          variant: 'striped',
          colorScheme: 'teal',
        }}
        title={t`Duyurular`}
        actions={isAdmin ? actionButtons : undefined}
        usePagination
      />
      <Modal isOpen={isDetailModalOpen} onClose={onDetailModalClose}>
        <ModalOverlay />
        <ModalContent>
          {focusedAnnouncement && (
            <>
              <ModalHeader>{focusedAnnouncement.title}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {isAdmin && (
                  <ExpertisePointTags
                    isAllSend={focusedAnnouncement.isAllSend}
                    expertisePoints={focusedAnnouncement.expertisePoints}
                  />
                )}
                {focusedAnnouncement.description}
              </ModalBody>
            </>
          )}
          <ModalFooter>
            <Button mr={3} onClick={onDetailModalClose}>
              <Trans>Kapat</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
